import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import StatusBadge from '../components/StatusBadge';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Search, UserPlus, Download } from 'lucide-react';

export default function Customers() {
  const { customers, alerts, tickets } = useApp();
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  const rows = useMemo(() => customers.map((c) => ({
    ...c,
    openAlerts: alerts.filter((a) => a.customerId === c.id && a.status !== 'resolved' && a.status !== 'dismissed').length,
    openTickets: tickets.filter((t) => t.customerId === c.id && !['completed', 'cancelled'].includes(t.status)).length,
  })), [customers, alerts, tickets]);

  const filtered = useMemo(() => rows.filter((c) => {
    if (!q) return true;
    const query = q.toLowerCase();
    return c.name.toLowerCase().includes(query) || c.address.toLowerCase().includes(query) || c.city.toLowerCase().includes(query) || c.email.toLowerCase().includes(query);
  }), [rows, q]);

  return (
    <div className="space-y-5" data-testid="customers-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Customers</div>
          <h1 className="mt-1 text-3xl font-semibold text-slate-900 tracking-tight">Customer Database</h1>
          <p className="mt-1 text-sm text-slate-500">{customers.length} residential & commercial accounts</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="h-9"><Download className="h-4 w-4 mr-2" />Export</Button>
          <Button className="h-9 bg-[#0052CC] hover:bg-[#0043A6]" data-testid="customers-add"><UserPlus className="h-4 w-4 mr-2" />Add Customer</Button>
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <div className="p-4 border-b border-slate-200">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, address, city, email..."
              className="pl-9 h-9 bg-slate-50 border-slate-200 focus-visible:bg-white"
              data-testid="customers-search"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-[10px] uppercase tracking-wider">Customer</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Address</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Phone</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Plan</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Devices</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Health</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Open Alerts</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Tickets</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">LTV</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Last Service</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => (
                <TableRow
                  key={c.id}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => navigate(`/customers/${c.id}`)}
                  data-testid={`customer-row-${c.id}`}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-md bg-slate-100 text-slate-700 text-xs font-semibold flex items-center justify-center">
                        {c.name.split(' ').map((n) => n[0]).slice(0, 2).join('')}
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">{c.name}</div>
                        <div className="text-[11px] text-slate-500 font-mono">{c.id}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">{c.address}, {c.city}</TableCell>
                  <TableCell className="text-xs font-mono text-slate-600">{c.phone}</TableCell>
                  <TableCell><StatusBadge status={c.plan === 'Premium Comfort Protection' ? 'warning' : c.plan === 'Preventive Maintenance' ? 'healthy' : c.plan === 'Basic Monitoring' ? 'info' : 'critical'}>{c.plan}</StatusBadge></TableCell>
                  <TableCell className="text-right font-mono text-sm">1</TableCell>
                  <TableCell><StatusBadge status={c.healthBand.toLowerCase()} /></TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {c.openAlerts > 0 ? <span className="text-red-600 font-semibold">{c.openAlerts}</span> : <span className="text-slate-400">0</span>}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">{c.openTickets}</TableCell>
                  <TableCell className="text-right font-mono text-sm">${c.lifetimeValue.toLocaleString()}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{c.lastServiceDate}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
